import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import PanToolIcon from '@mui/icons-material/PanTool';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { WorkflowNode } from '../../../types/backend';
import type { StagesRun, StepStatus } from '../utils/workflowStages';
import { grantLabel } from '../utils/toolGrants';

// Every status carries words as well as an icon and a colour, so it reads without either.
const STATUS: Record<
  StepStatus,
  { label: string; Icon: React.ComponentType<SvgIconProps>; color: SvgIconProps['color'] }
> = {
  done: { label: 'Done', Icon: CheckCircleIcon, color: 'success' },
  current: { label: 'In progress', Icon: PlayCircleOutlineIcon, color: 'primary' },
  waiting: { label: 'Waiting on you', Icon: PanToolIcon, color: 'warning' },
  pending: { label: 'Not started', Icon: RadioButtonUncheckedIcon, color: 'disabled' },
  skipped: { label: 'Skipped', Icon: RemoveCircleOutlineIcon, color: 'disabled' },
  failed: { label: 'Failed', Icon: ErrorOutlineIcon, color: 'error' },
};

const STATUS_TEXT: Record<StepStatus, string> = {
  done: 'success.main',
  current: 'primary.main',
  waiting: 'warning.main',
  pending: 'text.secondary',
  skipped: 'text.disabled',
  failed: 'error.main',
};

interface Props {
  node: WorkflowNode;
  labels: Map<string, string>;
  status?: StepStatus;
  run?: StagesRun;
  /** Controls set beside the label, such as the editor's edit and remove buttons. */
  actions?: React.ReactNode;
  /** Shown under the card's content, such as the editor's form for this step. */
  children?: React.ReactNode;
}

const DecisionRoutes: React.FC<{ node: WorkflowNode; run?: StagesRun }> = ({ node, run }) => {
  const taken = [...(run?.decisions ?? [])].reverse().find((d) => d.node_id === node.id);
  return (
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }} aria-label={`Routes from ${node.label}`}>
      {(node.branches ?? []).map((branch, i) => {
        const chosen = taken?.chosen_condition === branch.condition;
        const notTaken = taken !== undefined && !chosen;
        return (
          <Typography
            // Indexed: while a draft is edited, two routes can briefly share a condition.
            key={i}
            component="li"
            variant="caption"
            sx={{
              display: 'list-item',
              color: notTaken ? 'text.disabled' : 'text.secondary',
              textDecoration: notTaken ? 'line-through' : 'none',
              fontWeight: chosen ? 600 : 400,
            }}
          >
            If {branch.condition} {'→'} Stage {branch.target_layer}
            {chosen && ' (taken)'}
            {notTaken && ' (not taken)'}
          </Typography>
        );
      })}
      {taken?.reason && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Why: {taken.reason}
        </Typography>
      )}
    </Box>
  );
};

const WorkflowStepCard: React.FC<Props> = ({ node, labels, status, run, actions, children }) => {
  const muted = status === 'skipped';
  const meta = status ? STATUS[status] : undefined;
  const evidence = run?.evidence?.[node.id];
  const recorded = Object.entries(run?.facts?.[node.id] ?? {});
  const settled = status === 'done' || status === 'skipped';
  const owed = settled ? [] : (node.evidence_required ?? []).filter((key) => !run?.facts?.[node.id]?.[key]);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: status === 'failed' ? 'error.main' : 'divider',
        borderRadius: 1,
        p: 1.5,
        bgcolor: 'background.paper',
        minWidth: 0,
        opacity: muted ? 0.6 : 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
        {meta && <meta.Icon fontSize="small" color={meta.color} aria-hidden sx={{ mt: 0.25 }} />}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              overflowWrap: 'anywhere',
              color: muted ? 'text.disabled' : 'text.primary',
            }}
          >
            {node.label || 'Untitled step'}
          </Typography>
          {meta && status && (
            <Typography variant="caption" sx={{ display: 'block', color: STATUS_TEXT[status] }}>
              {meta.label}
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', overflowWrap: 'anywhere' }}
          >
            {node.goal}
          </Typography>

          {(node.gate || node.kind === 'decision' || (node.depends_on ?? []).length > 0) && (
            <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5} sx={{ mt: 0.75 }}>
              {node.gate && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  icon={<PanToolIcon />}
                  label="Needs your approval"
                />
              )}
              {node.kind === 'decision' && (
                <Chip size="small" variant="outlined" icon={<CallSplitIcon />} label="Chooses a route" />
              )}
              {(node.depends_on ?? []).map((dep) => {
                const label = labels.get(dep);
                if (label === undefined) {
                  throw new Error(`Step ${node.id} depends on ${dep}, which is not in this workflow`);
                }
                return <Chip key={dep} size="small" variant="outlined" label={`after ${label}`} />;
              })}
            </Stack>
          )}

          {(node.allowed_tools ?? []).length > 0 && (
            <Stack
              direction="row"
              useFlexGap
              flexWrap="wrap"
              spacing={0.5}
              sx={{ mt: 0.75 }}
              aria-label={`Tools ${node.label || 'this step'} may use`}
            >
              {(node.allowed_tools ?? []).map((entry) => (
                <Chip key={entry} size="small" variant="outlined" label={grantLabel(entry)} title={entry} />
              ))}
            </Stack>
          )}

          {node.kind === 'decision' && <DecisionRoutes node={node} run={run} />}

          {evidence && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}
            >
              {evidence}
            </Typography>
          )}

          {(recorded.length > 0 || owed.length > 0) && (
            <Box sx={{ mt: 0.5 }} aria-label={`Facts ${node.label || 'this step'} records`}>
              {recorded.map(([key, value]) => (
                <Typography
                  key={key}
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', overflowWrap: 'anywhere' }}
                >
                  {key}: {value}
                </Typography>
              ))}
              {owed.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', overflowWrap: 'anywhere' }}
                >
                  Must record: {owed.join(', ')}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
      </Stack>
      {children}
    </Box>
  );
};

export default WorkflowStepCard;
